import React, { useCallback, useState } from 'react';
import { InlineMath } from 'react-katex';
import { createApiUrl } from '../../../config/api';
import { useApiAuth } from '../../../hooks/useApiAuth';
import type { OptimizationProblem, VariableDef } from '../../../types/optimization';
import RangeSlider from './BoundsSlider';
import CollapsiblePanel from './CollapsiblePanel';
import './optimization.css';

// Add ChatMessage type for streaming messages
interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  id: string;
}

interface OptimizationProps {
  optimizationData: OptimizationProblem;
  userID?: string;
  sessionId?: string;
  onOptimizationMessage?: (message: ChatMessage) => void;
  onResponse?: (data: any[]) => void;
}

const Optimization: React.FC<OptimizationProps> = ({ 
  optimizationData, 
  userID, 
  sessionId,
  onOptimizationMessage,
  onResponse
}) => {
  const { makeAuthenticatedRequest } = useApiAuth();
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [editableVariables, setEditableVariables] = useState<VariableDef[]>(optimizationData?.variables || []);
  const [collapsedPanels, setCollapsedPanels] = useState<{[key: string]: boolean}>({
    objective: false,
    variables: false,
    constraints: false
  });

  // Update editable variables when optimizationData changes
  React.useEffect(() => {
    if (optimizationData?.variables) {
      setEditableVariables(optimizationData.variables);
    }
  }, [optimizationData]);

  // Variables change handler for child component
  const handleVariablesChange = (vars: VariableDef[]) => setEditableVariables(vars);

  // Toggle panel collapse
  const togglePanel = (panelKey: string) => {
    setCollapsedPanels(prev => ({
      ...prev,
      [panelKey]: !prev[panelKey]
    }));
  };

  // Handle optimization
  const handleOptimize = useCallback(async () => {
    if (!sessionId) {
      console.error('Missing sessionId for optimization');
      return;
    }

    setIsOptimizing(true);
    
    // Send initial optimization message immediately when user clicks optimize
    const initialMessageId = `optimization-${Date.now()}`;
    if (onOptimizationMessage) {
      onOptimizationMessage({
        role: 'assistant',
        content: 'Starting optimization...',
        id: initialMessageId
      });
    }
    
    try {
      console.log('Starting optimization with updated variables:', editableVariables);
      
      // Create the payload using the backend schema directly
      const optimizationPayload: OptimizationProblem = {
        problem_name: optimizationData.problem_name,
        variables: editableVariables,
        constraints: optimizationData.constraints,
        objective: optimizationData.objective
      };
      
      console.log('Sending optimization payload:', optimizationPayload);
      
      const response = await makeAuthenticatedRequest(createApiUrl(`/optimization/optimize_model/session_id/${sessionId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(optimizationPayload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }
      
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let messageId = initialMessageId; // Use the same ID to update the initial message
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim()) {
              // Handle AI SDK streaming protocol text tokens (format like 0:"text here")
              if (line.includes('"') && line.includes(':')) {
                try {
                  // Parse token to extract text from format like 0:"text here"
                  const colonIndex = line.indexOf(':');
                  if (colonIndex >= 0) {
                    const afterColon = line.slice(colonIndex + 1);
                    if (afterColon.startsWith('"') && afterColon.endsWith('"')) {
                      const text = afterColon.slice(1, -1); // Remove quotes
                      // Replace literal \n with actual new lines and Unicode escape sequences
                      let processedText = text.replace(/\\n/g, '\n');
                      // Handle Unicode escape sequences like \u00b2 (²)
                      processedText = processedText.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => {
                        return String.fromCharCode(parseInt(hex, 16));
                      });
                      accumulatedText += processedText;
                      
                      // Send text updates to chat
                      if (onOptimizationMessage) {
                        onOptimizationMessage({
                          role: 'assistant',
                          content: accumulatedText,
                          id: messageId
                        });
                      }
                    }
                  }
                } catch (e) {
                  console.error('Error parsing text token:', e);
                }
              }
            }
          }
        }
        
        // Send final completion message to replace any remaining optimization text
        if (onOptimizationMessage) {
          onOptimizationMessage({
            role: 'assistant',
            content: accumulatedText || 'Optimization completed successfully.',
            id: messageId
          });
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setIsOptimizing(false);
    }
  }, [sessionId, editableVariables, onResponse, optimizationData, onOptimizationMessage, makeAuthenticatedRequest]);

  if (!optimizationData) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="text-lg font-medium">No Optimization Problem</div>
          <div className="text-sm">Ask the AI to create an optimization problem</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Header with Optimize button */}
      <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-white to-gray-50/50 border-b border-gray-200/60 backdrop-blur-sm select-none flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-6 rounded-full" style={{ backgroundColor: 'rgb(30, 156, 215)' }}></div>
          <h2 
            className="text-xl font-light tracking-wide" 
            style={{ 
              fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', 
              color: 'rgb(30, 156, 215)',
              letterSpacing: '0.5px'
            }}
          >
            Optimization Problem
          </h2>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleOptimize}
            disabled={isOptimizing || !userID || !sessionId}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-300 select-none focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              isOptimizing || !userID || !sessionId
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-inner'
                : 'text-white hover:shadow-lg hover:scale-105 cursor-pointer transform active:scale-95'
            }`}
            style={{
              background: isOptimizing || !userID || !sessionId 
                ? undefined 
                : 'linear-gradient(135deg, rgba(30, 156, 215, 0.8) 0%, rgba(30, 156, 215, 0.9) 100%)',
              userSelect: 'none',
              boxShadow: isOptimizing || !userID || !sessionId 
                ? undefined 
                : '0 4px 15px rgba(30, 156, 215, 0.2)'
            }}
          >
            {isOptimizing ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Optimizing...</span>
              </div>
            ) : (
              'Optimize'
            )}
          </button>
        </div>
      </div>

      {/* Optimization Problem Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-6">
        <div className="max-w-4xl mx-auto space-y-6 pb-6">
          <CollapsiblePanel
            title="Problem Overview"
            isCollapsed={collapsedPanels.objective}
            onToggle={() => togglePanel('objective')}
          >
            <div className="space-y-3">
              <div>
                <span className="font-medium text-gray-600">Problem Name:</span>
                <span className="ml-2 text-gray-800">{optimizationData.problem_name}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Objective Function:</span>
                <div className="mt-1 p-3 bg-gray-50 rounded-md font-mono text-sm">
                  <div className="mb-1">
                    <span className="text-xs text-gray-500 uppercase">{optimizationData.objective.sense}</span>
                  </div>
                  {optimizationData.objective.expr}
                </div>
              </div>
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel
            title="Variables"
            isCollapsed={collapsedPanels.variables}
            onToggle={() => togglePanel('variables')}
          >
            <div className="space-y-6">
              {editableVariables.map((variable, index) => {
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-6 bg-gray-50 flex-shrink-0">
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Symbol</label>
                          <div className="bg-white border border-gray-300 rounded-md px-3 py-2 h-10 flex items-center variable-symbol-container">
                            {variable.standard_symbol ? (
                              <div className="optimization-katex-container">
                                <InlineMath math={variable.standard_symbol} />
                              </div>
                            ) : (
                              <span className="text-gray-500 italic">No symbol</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-grow">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <div className="bg-white border border-gray-300 rounded-md px-3 py-2 h-10 flex items-center">
                            <span className="text-gray-700 truncate">{variable.description || 'No description'}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bounds</label>
                        <div className="space-y-3">
                          {variable.bounds && variable.bounds.lower !== null && variable.bounds.upper !== null && (
                            <RangeSlider
                              initialBounds={{
                                lower: variable.bounds.lower ?? 0,
                                upper: variable.bounds.upper ?? 100
                              }}
                              value={{
                                lower: variable.bounds.lower ?? 0,
                                upper: variable.bounds.upper ?? 100
                              }}
                              onChange={(b) => {
                                const next = editableVariables.map((v, i) => (i === index ? { 
                                  ...v, 
                                  bounds: { lower: b.lower, upper: b.upper }
                                } : v));
                                handleVariablesChange(next);
                              }}
                              initialGuess={variable.initial_value}
                              onInitialGuessChange={(val) => {
                                const next = editableVariables.map((v, i) => (i === index ? { ...v, initial_value: val } : v));
                                handleVariablesChange(next);
                              }}
                              unitsLatex={variable.units}
                            />
                          )}
                          {(variable.bounds.lower === null || variable.bounds.upper === null) && (
                            <div className="text-sm text-gray-500 italic">
                              Unbounded variable (lower: {variable.bounds.lower ?? "∞"}, upper: {variable.bounds.upper ?? "∞"})
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel
            title="Constraints"
            isCollapsed={collapsedPanels.constraints}
            onToggle={() => togglePanel('constraints')}
          >
            <div className="space-y-2">
              {optimizationData.constraints && optimizationData.constraints.length > 0 ? (
                optimizationData.constraints?.map((constraint, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-md font-mono text-sm">
                    <div className="mb-1">
                      <span className="text-xs text-gray-500 uppercase">{constraint.type}</span>
                    </div>
                    {constraint.expr} {constraint.sense} 0
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No constraints defined</p>
              )}
            </div>
          </CollapsiblePanel>
        </div>
      </div>
    </div>
  );
};

export default Optimization;
